import { Pagination as MantinePagination, Select, Text } from '@mantine/core'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import classes from './Pagination.module.css'

type CPaginationProps = {
  totalPages: number
  currentPage: number
  resultsPerPage: number
  totalResults: number
  disablePerPage?: boolean
}

const perPageOptions = [10, 25, 50, 100].map(option => option.toString())

export const ListPagination = ({
  totalPages,
  currentPage,
  resultsPerPage,
  totalResults,
  disablePerPage,
}: CPaginationProps) => {
  const { i18n, t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const params = Object.fromEntries(searchParams)
  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language),
    [i18n.language],
  )
  const controlLabels = {
    first: t('generic.firstPage', { defaultValue: 'First page' }) as string,
    previous: t('generic.previousPage', {
      defaultValue: 'Previous page',
    }) as string,
    next: t('generic.nextPage', { defaultValue: 'Next page' }) as string,
    last: t('generic.lastPage', { defaultValue: 'Last page' }) as string,
  }
  const rangeStart =
    totalResults === 0 ? 0 : (currentPage - 1) * resultsPerPage + 1
  const rangeEnd = Math.min(currentPage * resultsPerPage, totalResults)

  const handlePageChange = (page: number) => {
    setSearchParams({ ...params, page: page.toString() })
  }

  const handlePerPageChange = (value: string | null) => {
    if (!value) return
    setSearchParams({ ...params, page: '1', results: value })
  }

  return (
    <div className={classes.root} data-testid="pagination">
      <div className={classes.controls}>
        {totalPages > 1 && (
          <MantinePagination
            boundaries={1}
            classNames={{ control: classes.pageControl }}
            gap={4}
            getControlProps={control => ({
              'aria-label': controlLabels[control],
              title: controlLabels[control],
            })}
            siblings={1}
            size="sm"
            total={totalPages}
            value={currentPage}
            withEdges
            onChange={handlePageChange}
          />
        )}

        {!disablePerPage && totalResults > 10 && (
          <div className={classes.perPage}>
            <Text className={classes.perPageLabel}>
              {t('generic.perPage', { defaultValue: 'Per page' })}
            </Text>
            <Select
              aria-label={
                t('generic.rowsPerPage', {
                  defaultValue: 'Rows per page',
                }) as string
              }
              classNames={{ input: classes.perPageInput }}
              data={perPageOptions}
              size="xs"
              value={resultsPerPage.toString()}
              w={80}
              onChange={handlePerPageChange}
            />
          </div>
        )}
      </div>

      <Text className={classes.summary}>
        {numberFormatter.format(rangeStart)}-{numberFormatter.format(rangeEnd)}{' '}
        {t('generic.of', { defaultValue: 'of' })}{' '}
        {numberFormatter.format(totalResults)}
      </Text>
    </div>
  )
}
